import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, retry, switchMap, tap, throwError } from 'rxjs';
import { unwrapApiResponse } from '@shared/api';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { RefrescoSesionService } from './refresco-sesion.service';
import {
  AccesoUsuario,
  Credenciales,
  EstadoSesion,
  SesionUsuario,
  SolicitudRestablecimiento,
} from './auth.types';

/**
 * Implementacion REAL del contrato, contra **ApiUserManager** (`/api/v1/sesion`).
 *
 * ── POR QUE DIRECTO Y NO POR UNA API PROPIA ────────────────────────────────
 *
 * `fadminportal` habla con su propia `AdminPortal.API`, que verifica las
 * credenciales contra este mismo servicio de servidor a servidor. Esa capa
 * existe alli porque **el portal emite su SESION PROPIA**: sus cookies, su
 * dominio, su modelo de mismo origen.
 *
 * Este login no tiene sesion propia que emitir — la sesion de la plataforma ES
 * la de ApiUserManager. Una capa intermedia aqui solo añadiria un salto y un
 * sitio mas donde el contrato se puede copiar mal.
 *
 * Consecuencia practica que conviene tener presente: en `fadminportal` las
 * cookies `sinay_acceso`/`sinay_refresco` aterrizan en el `HttpClient` del
 * SERVIDOR y nunca en el navegador, asi que alli no hay sesion de plataforma que
 * reemitir y `elegir-acceso` no se usa. Aqui el navegador SI la tiene, y por eso
 * este es el unico sitio donde elegir una tarjeta significa algo.
 *
 * La app NO manipula tokens en ningun momento: `credentialsInterceptor` pone
 * `withCredentials: true` y el navegador adjunta las cookies solo. Sin ese
 * interceptor **todo responde 401** aunque la sesion este perfectamente abierta
 * en el servidor, y no hay nada que lo delate.
 */

/** `AccesoDto` tal como viaja. Todo opcional: el respaldo lo decide `aAcceso`. */
interface AccesoCrudo {
  readonly rolCodigo?: string;
  readonly rolNombre?: string;
  readonly aplicacionCodigo?: string | null;
  readonly nombre?: string;
  readonly icono?: string;
  readonly url?: string;
  readonly esSuperadmin?: boolean;
}

/** `UsuarioSesionDto`. El servidor serializa TODO en camelCase. */
interface UsuarioCrudo {
  readonly id?: number | string;
  readonly login?: string;
  readonly nombre?: string;
  readonly nombreCompleto?: string;
  readonly correo?: string;
  readonly debeCambiarContrasena?: boolean;
}

/** `SesionDto`: el estado completo, en una sola respuesta. */
interface SesionCruda {
  readonly usuario?: UsuarioCrudo;
  readonly accesos?: readonly AccesoCrudo[];
  readonly accesoActivo?: AccesoCrudo | null;
}

/**
 * `nombreCompleto` lo compone el SERVIDOR y por eso se prefiere: si cada
 * aplicacion concatenara los apellidos por su cuenta, acabarian existiendo cinco
 * reglas distintas para los nulos. Se cae al `login` cuando viene vacio, porque
 * un hueco donde va el nombre parece sesion rota.
 */
function aUsuario(u: UsuarioCrudo): SesionUsuario {
  const login = u.login ?? '';
  return {
    id: String(u.id ?? ''),
    login,
    nombre: u.nombreCompleto?.trim() || u.nombre?.trim() || login,
    correo: u.correo ?? '',
    debeCambiarContrasena: u.debeCambiarContrasena === true,
  };
}

/**
 * `aplicacionCodigo` cae a `null` y NO a cadena vacia: `null` es el valor con
 * significado —la tarjeta del superadministrador— y colapsarlo con "" borraria
 * la distincion.
 */
function aAcceso(a: AccesoCrudo): AccesoUsuario {
  return {
    rolCodigo: a.rolCodigo ?? '',
    rolNombre: a.rolNombre ?? '',
    aplicacionCodigo: a.aplicacionCodigo ?? null,
    nombre: a.nombre ?? a.rolNombre ?? '',
    icono: a.icono ?? '',
    url: a.url ?? '',
    esSuperadmin: a.esSuperadmin === true,
  };
}

@Injectable()
export class AuthHttpService extends AuthService {
  private readonly http = inject(HttpClient);
  private readonly refresco = inject(RefrescoSesionService);
  private readonly base = `${environment.apiUrl}/api/v1/sesion`;

  private readonly estado = signal<EstadoSesion | null>(null);

  readonly sesion = computed(() => this.estado()?.usuario ?? null);
  readonly autenticado = computed(() => this.estado() !== null);
  readonly accesos = computed<readonly AccesoUsuario[]>(() => this.estado()?.accesos ?? []);
  readonly accesoActivo = computed(() => this.estado()?.accesoActivo ?? null);

  iniciarSesion(credenciales: Credenciales): Observable<void> {
    return this.http
      .post<unknown>(`${this.base}/autenticar`, {
        usuario: credenciales.usuario.trim(),
        //  El backend lo llama `contrasena`; el formulario, `password`. Mandar
        // `password` no falla: el backend recibe null y devuelve un error de
        // validacion que habla de otra cosa.
        contrasena: credenciales.password,
      })
      .pipe(
        unwrapApiResponse<SesionCruda>(),
        tap((s) => this.aplicar(s)),
        map(() => undefined),
      );
  }

  elegirAcceso(acceso: AccesoUsuario): Observable<AccesoUsuario> {
    return this.http
      .post<unknown>(`${this.base}/elegir-acceso`, {
        // Va el CODIGO del rol y no su id: el codigo es el contrato declarado
        // con el codigo fuente, el id es un detalle de la base.
        rolCodigo: acceso.rolCodigo,
        //  SE MANDA CUANDO LA TARJETA LO TIENE, Y SE OMITE CUANDO ES NULL.
        // Esa regla es la correcta y no es cosmetica.
        //
        // Omitir frente a mandar `null` da exactamente lo mismo —medido contra
        // el servicio: `System.Text.Json` deja la propiedad en null en los dos
        // casos, asi que `ResolverAcceso` ve lo mismo—. Se omite solo por no
        // meter ruido en el cuerpo.
        //
        // Lo que SI importa: para el superadministrador, `aplicacionCodigo: null`
        // **no significa "resuelvelo tu"**, significa "su tarjeta propia, la que
        // no pertenece a ninguna aplicacion" — y `ResolverAcceso` intenta la
        // coincidencia exacta antes de caer al candidato unico justamente por
        // eso. Si alguien "optimiza" esto omitiendo el campo TAMBIEN para las
        // tarjetas que si tienen aplicacion, el superadministrador acabaria
        // entrando siempre al portal, con 200 y sin ningun error.
        ...(acceso.aplicacionCodigo ? { aplicacionCodigo: acceso.aplicacionCodigo } : {}),
      })
      .pipe(
        unwrapApiResponse<SesionCruda>(),
        tap((s) => this.aplicar(s)),
        // Lo que devuelve el SERVIDOR, no la tarjeta que se pulso: ver la nota
        // del contrato. El respaldo a `acceso` solo cubre que el backend no lo
        // mande; nunca deberia ocurrir.
        map((s) => (s.accesoActivo ? aAcceso(s.accesoActivo) : acceso)),
      );
  }

  cambiarContrasena(actual: string, nueva: string): Observable<void> {
    return this.http
      .post<unknown>(`${this.base}/contrasena/cambiar`, {
        contrasenaActual: actual,
        contrasenaNueva: nueva,
      })
      .pipe(
        unwrapApiResponse<SesionCruda>(),
        tap((s) => this.aplicar(s)),
        map(() => undefined),
      );
  }

  /**
   * Pide el enlace de restablecimiento.
   *
   * NO lanza cuando la cuenta no existe: el servidor responde 200 con
   * `enviado: false` y su motivo. Tratarlo como error dejaria a la persona con
   * un mensaje generico justo en el caso que mas necesita explicacion — las
   * cuentas de buzon compartido, que no reciben correo nunca y cuya via es el
   * portal.
   */
  solicitarRestablecimiento(usuario: string): Observable<SolicitudRestablecimiento> {
    return this.http
      .post<unknown>(`${this.base}/contrasena/olvidada`, { usuario })
      .pipe(unwrapApiResponse<SolicitudRestablecimiento>());
  }

  /**
   * Completa el restablecimiento con el token del correo.
   *
   * NO abre sesion y es correcto: quien restablece vuelve a entrar por el login.
   * Abrirla convertiria un enlace de correo en una sesion activa sin que nadie
   * haya tecleado la contraseña nueva.
   */
  restablecerContrasena(token: string, nueva: string): Observable<void> {
    return this.http
      .post<unknown>(`${this.base}/contrasena/restablecer`, {
        token,
        contrasenaNueva: nueva,
      })
      .pipe(
        unwrapApiResponse<unknown>(),
        map(() => undefined),
      );
  }

  cerrarSesion(): Observable<void> {
    // El estado local se limpia ANTES y de forma optimista: deja de haber datos
    // a la vista de inmediato, sin esperar a la red.
    //
    // Pero limpiarlo NO CIERRA NADA. Las cookies son `HttpOnly`: solo el
    // servidor puede borrarlas, con el `Set-Cookie` de esta respuesta, y solo el
    // servidor puede revocar el refresco. Por eso el error se PROPAGA en vez de
    // tragarse — ver el contrato en `AuthService.cerrarSesion`.
    this.estado.set(null);

    return this.http.post<unknown>(`${this.base}/cerrar`, null).pipe(
      map(() => undefined),
      // Un reintento, que es lo que cubre el corte de red de un instante.
      //
      // Reintentar es seguro: `cerrar` es IDEMPOTENTE. Un segundo intento sobre
      // un refresco ya revocado afecta cero filas, responde 200 igual y no
      // ensucia la deteccion de reuso del servidor —que es una alarma de robo
      // real y no conviene llenar de ruido—.
      retry({ count: 1, delay: 400 }),
    );
  }

  restaurarSesion(): Observable<void> {
    // No hay nada que inspeccionar en el cliente: la cookie es `HttpOnly`.
    const actual = () => this.http.get<unknown>(`${this.base}/actual`);
    return actual().pipe(
      // Un 401 al arrancar solo dice que caduco la cookie de ACCESO (dura 10
      // minutos); el refresco puede seguir vivo. Hasta el 2026-10-19 aqui se
      // pintaba el formulario aunque la sesion siguiera abierta, igual que en
      // `session-core` hasta la 5.2.25. `/sesion/actual` esta excluida del
      // interceptor de refresco a proposito, asi que la renovacion va aqui.
      catchError((error: unknown) =>
        error instanceof HttpErrorResponse && error.status === 401
          ? this.refresco.refrescar().pipe(switchMap(() => actual()))
          : throwError(() => error),
      ),
      unwrapApiResponse<SesionCruda>(),
      tap((s) => this.aplicar(s)),
      map(() => undefined),
      // Sin cookie, caducada, o backend caido: se arranca sin sesion, que es el
      // estado seguro. Esto NUNCA puede tumbar el arranque de la app.
      catchError(() => {
        this.estado.set(null);
        return of(undefined);
      }),
    );
  }

  /**
   * Aplica una sesion recien llegada: usuario, accesos y acceso activo en UNA
   * sola operacion.
   *
   * Es un solo punto a proposito. Con el usuario puesto en un sitio y los
   * accesos en otro, basta que un camino nuevo —otro endpoint, otro refresco—
   * olvide uno de los dos para que la rejilla salga vacia sin motivo aparente.
   * Aqui o llegan los tres o no llega ninguno.
   */
  private aplicar(s: SesionCruda): void {
    if (!s.usuario) {
      this.estado.set(null);
      return;
    }
    this.estado.set({
      usuario: aUsuario(s.usuario),
      accesos: (s.accesos ?? []).map(aAcceso),
      accesoActivo: s.accesoActivo ? aAcceso(s.accesoActivo) : null,
    });
  }
}
